import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, FileText, RefreshCw, ArrowUp, ArrowDown, AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "../lib/api.js";
import { fmtMinor, fmtHourly } from "../lib/money.js";
import { LoadError } from "../components/LoadError.jsx";
import { PageHeader, Spinner, EmptyState, timeAgo } from "../components/ui.jsx";

// Operator view: every service and database any client has deployed, in one table.
// The point of the page is the two things that should never be true and are otherwise
// invisible — something running with no plan (unbilled), and something with no owner.

const NO_OWNER = "__none";
const TYPE_LABEL = { application: "Service", database: "Database" };
const STATUS_TONE = {
  running: "pill-ok", healthy: "pill-ok",
  building: "pill-warn", deploying: "pill-warn", in_progress: "pill-warn", queued: "pill-warn", degraded: "pill-warn", restarting: "pill-warn", starting: "pill-warn",
  exited: "pill-err", failed: "pill-err", error: "pill-err", unhealthy: "pill-err", missing: "pill-err",
};
const titleCase = (v) => String(v || "unknown").replace(/[_-]+/g, " ").replace(/^\w/, (c) => c.toUpperCase());

// Same zone-less-means-UTC rule as timeAgo(), so sorting agrees with what is displayed.
function ts(iso) {
  if (!iso) return 0;
  const str = String(iso);
  const d = /(Z|[+-]\d\d:?\d\d)$/.test(str) ? new Date(str) : new Date(str.replace(" ", "T") + "Z");
  const n = d.getTime();
  return Number.isNaN(n) ? 0 : n;
}
const host = (domain) => String(domain || "").split(",")[0].trim().replace(/^https?:\/\//i, "").replace(/\/.*$/, "");
// A price is only shown for something that actually has a plan. "£0.00" next to a service
// with no plan would read as a free tier, which does not exist.
const priced = (r) => !r.unpriced && r.monthlyPence != null && !!(r.planId || r.planName || Number(r.monthlyPence) > 0);
const detailPath = (sv) => (sv.type === "database" ? `/databases/${sv.uuid}` : `/services/${sv.uuid}`);

const COLUMNS = [
  { key: "client", label: "Client", get: (r) => (r.org?.name || "").toLowerCase() },
  { key: "name", label: "Service", get: (r) => (r.name || "").toLowerCase() },
  { key: "type", label: "Type", get: (r) => r.type || "" },
  { key: "status", label: "Status", get: (r) => r.status || "" },
  { key: "plan", label: "Plan", get: (r) => (r.unpriced ? "" : (r.planName || r.planId || "").toLowerCase()) },
  { key: "monthly", label: "£/month", numeric: true, get: (r) => (priced(r) ? Number(r.monthlyPence) || 0 : -1) },
  { key: "server", label: "Server", get: (r) => (r.server?.name || r.server?.ip || "").toLowerCase() },
  { key: "domain", label: "Domain", get: (r) => host(r.domain).toLowerCase() },
  { key: "deployed", label: "Last deployed", numeric: true, get: (r) => ts(r.lastDeployedAt) },
];

function csvDownload(rows) {
  const head = ["client", "client_slug", "owner_email", "service", "type", "uuid", "status", "health", "plan", "monthly_gbp", "comp", "unpriced", "server", "server_ip", "domain", "created_at", "last_deployed_at"];
  const body = rows.map((r) => [
    r.org?.name || "", r.org?.slug || "", r.ownerEmail || "", r.name, r.type, r.uuid, r.status, r.health || "",
    r.planName || r.planId || "", priced(r) ? ((Number(r.monthlyPence) || 0) / 100).toFixed(2) : "",
    r.comp ? "yes" : "no", r.unpriced ? "yes" : "no", r.server?.name || "", r.server?.ip || "", host(r.domain), r.createdAt || "", r.lastDeployedAt || "",
  ]);
  const csv = [head, ...body].map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = "client-services.csv"; a.click();
  URL.revokeObjectURL(url);
}

function Tile({ label, value, tone, hint, active, onClick }) {
  const color = tone === "warn" ? "var(--warn-text)" : tone === "err" ? "var(--err-text)" : "var(--text)";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined} onClick={onClick} title={hint} aria-pressed={onClick ? !!active : undefined}
      style={{
        textAlign: "left", fontFamily: "inherit", cursor: onClick ? "pointer" : "default", minWidth: 0,
        background: "var(--surface)", border: `1px solid ${active ? "var(--accent)" : "var(--border)"}`, borderRadius: 10, padding: "12px 14px",
        boxShadow: "var(--shadow)",
      }}
    >
      <div style={{ fontSize: 11.5, color: "var(--text-muted)", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, color, fontVariantNumeric: "tabular-nums", letterSpacing: "-.01em", marginTop: 3 }}>{value}</div>
    </Tag>
  );
}

function StatusCell({ row }) {
  const st = String(row.status || "unknown");
  const tone = STATUS_TONE[st] || "pill-neutral";
  const unhealthy = row.health && /unhealthy/i.test(row.health) && st !== "missing";
  return (
    <span className={`pill ${unhealthy ? "pill-err" : tone}`} style={{ whiteSpace: "nowrap" }}
      title={st === "missing" ? "Owned by this client in the panel, but no longer exists on the platform" : row.health ? `Health: ${row.health}` : undefined}>
      {st === "missing" ? "Missing" : titleCase(st)}{unhealthy ? " · unhealthy" : ""}
    </span>
  );
}

export default function ClientServices() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [client, setClient] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [onlyUnpriced, setOnlyUnpriced] = useState(false);
  const [onlyUnowned, setOnlyUnowned] = useState(false);
  const [sort, setSort] = useState({ key: "client", dir: 1 });
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = () => {
    setLoading(true);
    return api.adminClientServices()
      .then((d) => { if (alive.current) { setData({ ...d, services: Array.isArray(d?.services) ? d.services : [] }); setError(null); } })
      .catch((e) => { if (alive.current) setError(e); }) // keep the last good table on screen
      .finally(() => { if (alive.current) setLoading(false); });
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const services = data?.services || [];

  const clients = useMemo(() => {
    const m = new Map();
    for (const r of services) if (r.org?.id != null) m.set(String(r.org.id), r.org.name || r.org.slug || `Org ${r.org.id}`);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [services]);
  const statuses = useMemo(() => [...new Set(services.map((r) => String(r.status || "unknown")))].sort(), [services]);

  // Totals come from the server; anything it didn't send is counted from the rows so a tile
  // is never blank (or, worse, a confident zero) just because a field was missing.
  const totals = useMemo(() => {
    const t = data?.totals || {};
    const apps = services.filter((r) => r.type !== "database");
    const dbs = services.filter((r) => r.type === "database");
    return {
      services: t.services ?? apps.length,
      databases: t.databases ?? dbs.length,
      clients: t.clients ?? new Set(services.filter((r) => r.org?.id != null).map((r) => r.org.id)).size,
      monthlyPence: t.monthlyPence ?? services.reduce((sum, r) => sum + (r.unpriced ? 0 : Number(r.monthlyPence) || 0), 0),
      unpriced: t.unpriced ?? services.filter((r) => r.unpriced).length,
      unowned: t.unowned ?? services.filter((r) => !r.org).length,
    };
  }, [data, services]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const col = COLUMNS.find((c) => c.key === sort.key) || COLUMNS[0];
    return services
      .filter((r) => !needle || [r.name, r.org?.name, r.org?.slug, r.ownerEmail, r.domain, r.uuid].some((v) => String(v || "").toLowerCase().includes(needle)))
      .filter((r) => !client || (client === NO_OWNER ? !r.org : String(r.org?.id) === client))
      .filter((r) => !type || r.type === type)
      .filter((r) => !status || String(r.status || "unknown") === status)
      .filter((r) => !onlyUnpriced || r.unpriced)
      .filter((r) => !onlyUnowned || !r.org)
      .map((r, i) => [r, i])
      .sort(([a, ai], [b, bi]) => {
        const x = col.get(a), y = col.get(b);
        const cmp = col.numeric ? x - y : String(x).localeCompare(String(y));
        return (cmp || ai - bi) * (cmp ? sort.dir : 1);
      })
      .map(([r]) => r);
  }, [services, q, client, type, status, onlyUnpriced, onlyUnowned, sort]);

  const filtered = !!(q.trim() || client || type || status || onlyUnpriced || onlyUnowned);
  const clear = () => { setQ(""); setClient(""); setType(""); setStatus(""); setOnlyUnpriced(false); setOnlyUnowned(false); };
  const toggleSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const selectStyle = { width: "auto", minWidth: 130, maxWidth: "100%", flex: "0 1 auto" };
  const quick = (on) => ({
    display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px", borderRadius: 999, fontFamily: "inherit",
    fontSize: 12.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
    border: `1px solid ${on ? "var(--warn)" : "var(--border-strong)"}`,
    background: on ? "var(--warn-soft)" : "var(--surface)", color: on ? "var(--warn-text)" : "var(--text)",
  });
  const th = { textAlign: "left", padding: "9px 12px", fontSize: 11.5, fontWeight: 600, color: "var(--text-muted)", whiteSpace: "nowrap", borderBottom: "1px solid var(--border)", background: "var(--surface-2)" };
  const td = { padding: "10px 12px", fontSize: 13, color: "var(--text)", borderBottom: "1px solid var(--border)", verticalAlign: "middle" };

  return (
    <div className="page" style={{ maxWidth: 1400 }}>
      <PageHeader
        title="Client services"
        subtitle={`Every service and database deployed by clients${data?.generatedAt ? ` · updated ${timeAgo(data.generatedAt)}` : ""}.`}
        actions={
          <>
            <button className="btn btn-secondary" onClick={load} disabled={loading}>
              {loading ? <Spinner /> : <RefreshCw size={14} />} Refresh
            </button>
            <button className="btn btn-secondary" onClick={() => csvDownload(shown)} disabled={!data || shown.length === 0} title="Exports the rows currently shown">
              <FileText size={14} /> Export CSV
            </button>
          </>
        }
      />

      {error && <div className="mb-4"><LoadError error={error} onRetry={load} what="client services" /></div>}

      {!data && !error && (
        <div className="flex h-40 items-center justify-center" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2" /> Loading client services…</div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-3 lg:grid-cols-6">
            <Tile label="Services" value={totals.services} />
            <Tile label="Databases" value={totals.databases} />
            <Tile label="Clients" value={totals.clients} />
            <Tile label="Monthly total" value={fmtMinor(totals.monthlyPence)} hint="Sum of the monthly plan price of everything listed" />
            <Tile label="Unpriced" value={totals.unpriced} tone={totals.unpriced > 0 ? "warn" : undefined} hint="Running with no paid plan — click to filter"
              active={onlyUnpriced} onClick={() => setOnlyUnpriced((v) => !v)} />
            <Tile label="No owner" value={totals.unowned} tone={totals.unowned > 0 ? "warn" : undefined} hint="Not attached to any client — click to filter"
              active={onlyUnowned} onClick={() => setOnlyUnowned((v) => !v)} />
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div style={{ position: "relative", flex: "1 1 220px", minWidth: 0, maxWidth: 320 }}>
              <Search size={15} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: "var(--text-muted)" }} />
              <input className="input" style={{ paddingLeft: 33 }} placeholder="Search name, client or domain" aria-label="Search services" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <select className="select" style={selectStyle} aria-label="Filter by client" value={client} onChange={(e) => setClient(e.target.value)}>
              <option value="">All clients</option>
              {clients.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              <option value={NO_OWNER}>No owner</option>
            </select>
            <select className="select" style={selectStyle} aria-label="Filter by type" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">All types</option>
              <option value="application">Services</option>
              <option value="database">Databases</option>
            </select>
            <select className="select" style={selectStyle} aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {statuses.map((st) => <option key={st} value={st}>{titleCase(st)}</option>)}
            </select>
            <button type="button" style={quick(onlyUnpriced)} aria-pressed={onlyUnpriced} onClick={() => setOnlyUnpriced((v) => !v)}>
              <AlertTriangle size={13} /> Unpriced (running free)
            </button>
            <button type="button" style={quick(onlyUnowned)} aria-pressed={onlyUnowned} onClick={() => setOnlyUnowned((v) => !v)}>
              No owner
            </button>
            {filtered && <button type="button" className="btn btn-ghost" style={{ padding: "5px 10px" }} onClick={clear}>Clear filters</button>}
            <span style={{ marginLeft: "auto", fontSize: 12.5, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
              {shown.length === services.length ? `${services.length} total` : `${shown.length} of ${services.length}`}
            </span>
          </div>

          {services.length === 0 ? (
            <div className="card"><EmptyState title="No client services yet" description="Nothing has been deployed by any client. Services and databases appear here as soon as they are created." /></div>
          ) : shown.length === 0 ? (
            <div className="card"><EmptyState title="Nothing matches" description="No service or database matches these filters." action={<button className="btn btn-secondary" onClick={clear}>Clear filters</button>} /></div>
          ) : (
            <div style={{ border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface)", boxShadow: "var(--shadow)", overflowX: "auto" }}>
              <table style={{ width: "100%", minWidth: 1040, borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    {COLUMNS.map((c) => {
                      const on = sort.key === c.key;
                      return (
                        <th key={c.key} style={{ ...th, textAlign: c.key === "monthly" ? "right" : "left" }} aria-sort={on ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
                          <button type="button" onClick={() => toggleSort(c.key)}
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", padding: 0, font: "inherit", cursor: "pointer", color: on ? "var(--text)" : "var(--text-muted)" }}>
                            {c.label}
                            {on ? (sort.dir === 1 ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <span style={{ width: 12 }} />}
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const h = host(r.domain);
                    return (
                      <tr key={`${r.type}:${r.uuid}`} style={r.unpriced ? { background: "color-mix(in srgb, var(--warn) 6%, transparent)" } : undefined}>
                        <td style={td}>
                          {r.org ? (
                            <div style={{ minWidth: 0, maxWidth: 200 }}>
                              <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.org.name}>{r.org.name || r.org.slug}</div>
                              {r.ownerEmail && <div style={{ fontSize: 11.5, color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.ownerEmail}>{r.ownerEmail}</div>}
                            </div>
                          ) : (
                            <span className="pill pill-err" title="This is not attached to any client account, so nobody is being billed for it">No owner</span>
                          )}
                        </td>
                        <td style={td}>
                          {r.status === "missing"
                            ? <span style={{ fontWeight: 600 }}>{r.name || r.uuid}</span>
                            : <Link to={detailPath(r)} style={{ fontWeight: 600, color: "var(--accent-text)", textDecoration: "none" }}>{r.name || r.uuid}</Link>}
                        </td>
                        <td style={{ ...td, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{TYPE_LABEL[r.type] || titleCase(r.type)}</td>
                        <td style={td}><StatusCell row={r} /></td>
                        <td style={td}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            {r.unpriced
                              ? <span className="pill pill-warn" style={{ whiteSpace: "nowrap" }}><AlertTriangle size={12} /> No plan — running free</span>
                              : <span style={{ whiteSpace: "nowrap" }}>{r.planName || r.planId || "—"}</span>}
                            {r.comp && <span className="pill pill-neutral" title="Complimentary — the client is not charged for this">Comp</span>}
                          </div>
                        </td>
                        <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", color: r.unpriced ? "var(--warn-text)" : "var(--text)" }}>
                          {priced(r) ? fmtMinor(r.monthlyPence) : "—"}
                          {priced(r) && Number(r.monthlyPence) > 0 && (
                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{fmtHourly(r.monthlyPence / 100, "£")}</div>
                          )}
                        </td>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>
                          {r.server ? (
                            <>
                              <div>{r.server.name || r.server.ip || "—"}</div>
                              {r.server.name && r.server.ip && <div className="mono" style={{ fontSize: 11, color: "var(--text-muted)" }}>{r.server.ip}</div>}
                            </>
                          ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                        </td>
                        <td style={td}>
                          {h ? (
                            <a href={`https://${h}`} target="_blank" rel="noreferrer" className="mono"
                              style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--text)", textDecoration: "none", maxWidth: 220 }} title={h}>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h}</span>
                              <ExternalLink size={11} style={{ flexShrink: 0, color: "var(--text-muted)" }} />
                            </a>
                          ) : <span style={{ color: "var(--text-muted)" }}>—</span>}
                        </td>
                        <td style={{ ...td, color: "var(--text-muted)", whiteSpace: "nowrap" }} title={r.lastDeployedAt || undefined}>
                          {r.lastDeployedAt ? timeAgo(r.lastDeployedAt) : "Never"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
